import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const STORAGE_KEY = 'dev-ribbon-dismissed';
const TITLE_PREFIX = '[DEV] ';
const RIBBON_HEIGHT = 28;

function setDevFavicon() {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#FDD835';
  ctx.fillRect(0, 0, 32, 32);
  ctx.fillStyle = '#212121';
  ctx.font = 'bold 14px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('DEV', 16, 17);

  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = canvas.toDataURL('image/png');
}

/**
 * Yellow development indicator: fixed top ribbon + [DEV] tab title/favicon.
 * Only mounts when NODE_ENV === 'development'. Dismissible; reopen via edge tab.
 */
export default function DevModeRibbon() {
  const isDev = process.env.NODE_ENV === 'development';
  const [visible, setVisible] = useState(() => {
    if (!isDev) return false;
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!isDev) return undefined;

    const baseTitle = document.title.replace(TITLE_PREFIX, '');
    document.title = `${TITLE_PREFIX}${baseTitle}`;
    setDevFavicon();

    return () => {
      document.title = baseTitle;
    };
  }, [isDev]);

  if (!isDev) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const reopen = () => {
    setVisible(true);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  if (!visible) {
    return (
      <Box
        component="button"
        type="button"
        onClick={reopen}
        aria-label="Show development ribbon"
        sx={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 99999,
          height: 18,
          px: 1.5,
          border: 'none',
          borderRadius: '0 0 6px 6px',
          bgcolor: '#FDD835',
          color: '#212121',
          cursor: 'pointer',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          lineHeight: 1,
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          '&:hover': { bgcolor: '#FBC02D' },
        }}
      >
        DEV
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-label="Development environment"
      sx={{
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '50%',
        maxWidth: '50vw',
        height: RIBBON_HEIGHT,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        bgcolor: '#FDD835',
        color: '#212121',
        borderRadius: '0 0 8px 8px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        pointerEvents: 'auto',
      }}
    >
      <WarningAmberIcon sx={{ fontSize: 16 }} />
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        Development
      </Typography>
      <IconButton
        size="small"
        onClick={dismiss}
        aria-label="Hide development ribbon"
        sx={{
          position: 'absolute',
          right: 4,
          color: '#212121',
          p: 0.25,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
